import React, { Fragment, ReactNode, useState } from "react";
import classNames from "classnames";
import { MobileViewTable } from "./MovileView";
import { processColumnConfigs } from "../../ColumnSelect/ColumnUtil";
import { PaginationProps } from "../../Pagination";
import Button from "../../Button";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { whiteLabelStyling } from "../../../../shared/utils/constants";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import TableShimmer from "./TableShimmer";

export interface Column {
  name: string;
  key: string;
  isHidden?: boolean;
  isDefault?: boolean;
  isVisible?: boolean;
  showInMobile?: boolean;
  width?: string;
  headerStyles?: string;
  rowStyles?: string;
  zIndex?: number;
  onClick?: (event: any, col: any) => void;
  sortable?: boolean;
}

export interface TableProps<T> {
  rows: T;
  columns: Array<Column>;
  onRowClick?: (row: any, event: any) => void;
  className?: string;
  hasEditColumns?: boolean;
  styles?: {
    table?: string;
    thead?: string;
    tbody?: string;
    tableContainer?: string;
  };
  emptyText: string | JSX.Element;
  MobileRow?: (row: any) => JSX.Element;
  visibleColumns?: Array<string>;
  setVisibleColumns?: Function;
  hasExpandableSection?: boolean;
  isLoading?: boolean;
  columnAction?: (visibleColumns: string, prevVisibleColumn?: string[]) => void;
  expandableContent?: (...args: any) => JSX.Element;
  wrap: boolean;
  onSort?: (sortKey: string, sortDirection: "asc" | "desc") => void;
  sort?: { key: string; direction: "asc" | "desc" };
  noPaddingX?: boolean;
}

interface TableRowProps<T> {
  columns: Array<Column>;
  rowItem: T;
  onClick?: React.MouseEventHandler<HTMLTableRowElement>;
  hasExpandableSection?: boolean;
  expandableContent: (...args: any) => ReactNode;
  wrap: boolean;
}

const TableRow = ({
  columns,
  rowItem,
  onClick,
  hasExpandableSection = false,
  expandableContent,
  wrap
}: TableRowProps<{ styles?: any } | any>) => {
  const { whiteLabelStyle = {} } = whiteLabelStyling();
  const [isOpen, setIsOpen] = useState<boolean>(false);

  return (
    <>
      <tr
        className={classNames(
          onClick ? "cursor-pointer" : "",
          "overflow-visible",
          whiteLabelStyle?.theme?.hover?.bg || "hover:bg-indigo-50",
          rowItem?.styles?.tr ?? ""
        )}
        onClick={onClick}
      >
        {columns
          ?.filter((c) => c.isDefault || (!c.isDefault && !c.isHidden))
          ?.map((col, index) => (
            <td
              key={index}
              id={col.key}
              style={{
                minWidth: col?.width ?? "",
                maxWidth: col?.width ?? "",
                width: col?.width ?? "",
                zIndex: col?.zIndex ? col?.zIndex - rowItem["index"] : ""
              }}
              className={classNames(
                "border-b border-gray-200 px-3 text-xs text-gray-500",
                rowItem?.styles?.[col.key] ?? "py-4",
                // col?.rowStyles?.includes("remove-truncate") ? "" : "truncate",
                col?.rowStyles ?? "",
                wrap
                  ? "break-words whitespace-normal"
                  : "overflow-visible truncate whitespace-nowrap"
              )}
              onClick={(e: any) => {
                if (!col?.onClick) return;
                col?.onClick(e, col);
              }}
            >
              {rowItem[col.key] || "-"}
            </td>
          ))}
        {hasExpandableSection && (
          <td className="border-b border-gray-200 whitespace-nowrap px-3 text-xs text-gray-500 overflow-visible truncate w-[80px]">
            <Button
              className="px-2 !bg-transparent"
              onClick={() => {
                setIsOpen((prevState) => !prevState);
              }}
            >
              <ChevronDownIcon
                className={classNames(
                  "w-4 h-4 transition-transform duration-150",
                  isOpen ? "rotate-180" : ""
                )}
              />
            </Button>
          </td>
        )}
      </tr>
      {hasExpandableSection && isOpen ? (
        <tr>
          <td
            colSpan={100}
            className="bg-gray-50"
          >
            {expandableContent(rowItem)}
          </td>
        </tr>
      ) : null}
    </>
  );
};

const GenericTable = <T extends []>({
  rows,
  columns,
  onRowClick,
  className,
  styles = {},
  emptyText = "",
  MobileRow,
  visibleColumns = [],
  pagination,
  hasExpandableSection = false,
  isLoading,
  expandableContent,
  columnAction,
  wrap,
  onSort,
  sort,
  noPaddingX = false
}: TableProps<T> & { pagination?: PaginationProps }) => {
  const [prevVisibleColumn, setPreVisibleColumn] = React.useState(
    [] as string[]
  );
  columns = processColumnConfigs(columns, visibleColumns);

  React.useEffect(() => {
    if (columnAction) {
      addOrRemoveColumn();
    }
  }, [visibleColumns]);

  function addOrRemoveColumn() {
    if (prevVisibleColumn.length === visibleColumns.length) {
      return;
    }
    if (visibleColumns?.length > prevVisibleColumn?.length) {
      const nameOfColumn = getColumn(
        visibleColumns?.[visibleColumns?.length - 1]
      );
      const message = `${nameOfColumn?.name} has been added to listing`;
      columnAction?.(message, prevVisibleColumn);
      setPreVisibleColumn((prev) => [...prev, nameOfColumn?.key || ""]);
    } else if (visibleColumns?.length < prevVisibleColumn?.length) {
      const notExist = prevVisibleColumn?.find(
        (item) => !visibleColumns?.includes(item)
      );
      const nameOfColumn = getColumn(notExist || "");
      const message = `${nameOfColumn?.name} has been removed from listing`;
      columnAction?.(message, prevVisibleColumn);
      setPreVisibleColumn(() => [...visibleColumns]);
    }
  }

  const getColumn = (key: string) => {
    return columns?.find((item) => item?.key === key);
  };

  const handleSort = (columnKey: string) => {
    if (!onSort) return;

    const newSortDirection =
      sort?.key === columnKey && sort?.direction === "asc" ? "desc" : "asc";
    onSort(columnKey, newSortDirection);
  };

  const isMobile = window.innerWidth < 640;

  return (
    <>
      {Object.keys(pagination ?? {})?.length > 0 ? (
        <div
          className={classNames(
            "max-w-full sm:max-h-[65vh] overflow-auto rounded-lg sm:hidden bg-white sm:bg-white md:px-0",
            noPaddingX ? "" : "px-2",
            className || ""
          )}
        >
          <MobileViewTable
            rows={rows}
            MobileRow={MobileRow}
            noDataPlaceholder={emptyText}
            pagination={pagination!}
          />
        </div>
      ) : null}

      {!isMobile && (
        <div
          className={classNames(
            "max-w-full max-h-screen h-full overflow-auto rounded-lg hidden md:block",
            styles.tableContainer || ""
          )}
        >
          {isLoading ? (
            <TableShimmer
              columns={
                columns.filter(
                  (c) => c.isDefault || (!c.isDefault && !c.isHidden)
                ).length
              }
            />
          ) : rows?.length > 0 ? (
            <table
              className={classNames(
                "min-w-full border-separate relative",
                styles.table || ""
              )}
              style={{ borderSpacing: 0 }}
            >
              <thead className={classNames(styles.thead || "")}>
                <tr>
                  {columns
                    ?.filter(
                      (c) => c.isDefault || (!c.isDefault && !c.isHidden)
                    )
                    .map((col, colIdx) => {
                      return (
                        <Fragment key={colIdx}>
                          <th
                            key={colIdx}
                            // key={col.key}
                            scope="col"
                            className={classNames(
                              "sticky top-0 border-b border-gray-200 whitespace-nowrap bg-white px-3 py-4 text-left text-xs font-semibold text-gray-900 z-10",
                              col?.headerStyles?.includes("remove-truncate")
                                ? ""
                                : "truncate",
                              col?.headerStyles ?? "",
                              col.sortable ? "cursor-pointer" : ""
                            )}
                            style={{ width: col?.width || "" }}
                            onClick={() => col.sortable && handleSort(col.key)}
                          >
                            <div className="flex items-center">
                              {col.name}
                              {col.sortable && (
                                <span className="ml-2">
                                  {sort?.key === col.key ? (
                                    sort.direction === "asc" ? (
                                      <FaSortUp />
                                    ) : (
                                      <FaSortDown />
                                    )
                                  ) : (
                                    <FaSort />
                                  )}
                                </span>
                              )}
                            </div>
                          </th>
                        </Fragment>
                      );
                    })}

                  {hasExpandableSection ? (
                    <th className="sticky top-0 border-b border-gray-200 whitespace-nowrap bg-white px-3 py-4 text-left text-xs font-semibold text-gray-900 truncate z-10"></th>
                  ) : null}
                </tr>
              </thead>

              <tbody
                className={classNames(
                  "bg-white overflow-visible",
                  styles.tbody || ""
                )}
              >
                {rows.map((r, rIndex) => (
                  <Fragment key={rIndex}>
                    <TableRow
                      columns={columns}
                      rowItem={r}
                      key={rIndex}
                      onClick={(e) => onRowClick?.(r, e)}
                      hasExpandableSection={hasExpandableSection}
                      expandableContent={expandableContent!}
                      wrap={wrap}
                    />
                  </Fragment>
                ))}
              </tbody>
            </table>
          ) : (
            emptyText
          )}
        </div>
      )}
    </>
  );
};

export default GenericTable;
